import type { Course } from "@brookbush/types";

import { useUser } from "@clerk/nextjs";
import posthog from "posthog-js";
import { useEffect } from "react";
import { toast } from "react-hot-toast";

import ConfirmationModal from "~/components/ConfirmationModal";
import useFreeCourse from "~/hooks/useFreeCourse";
import useFreeCourseClaimModal from "~/hooks/useFreeCourseClaimModal";
import {
	FREE_COURSE_COPY,
	freeCoursePromptKey,
	shouldPromptAfterSignup,
} from "~/lib/free-course";

interface CourseFreeCourseClaimModalProps {
	course: Course;
}

/**
 * "Use your free course on …?" confirmation. Also opens on its own once,
 * right after someone signs up from this course.
 */
const CourseFreeCourseClaimModal = ({
	course,
}: CourseFreeCourseClaimModalProps) => {
	const { state, claim } = useFreeCourse(course._id);
	const { isOpen, open, close } = useFreeCourseClaimModal();
	const { user } = useUser();

	const signupCourseId = user?.unsafeMetadata?.auditedCourseId;

	useEffect(() => {
		const key = freeCoursePromptKey(course._id);

		if (
			shouldPromptAfterSignup({
				state,
				signupCourseId,
				courseId: course._id,
				dismissed: sessionStorage.getItem(key) === "1",
			})
		) {
			sessionStorage.setItem(key, "1");
			open();
		}
	}, [state, signupCourseId, course._id, open]);

	useEffect(() => close, [close]);

	return (
		<ConfirmationModal
			open={isOpen && state.kind === "offer"}
			onClose={close}
			title={FREE_COURSE_COPY.confirmTitle(course.title)}
			titleClassName="line-clamp-none"
			description={FREE_COURSE_COPY.confirmBody}
			confirmationLabel={FREE_COURSE_COPY.confirmYes}
			cancellationLabel={FREE_COURSE_COPY.confirmNo}
			onConfirm={async () => {
				posthog.capture("free_course:claim_confirmed", {
					course_id: course._id,
				});

				try {
					await claim();
					close();
				} catch {
					toast.error("We couldn't unlock this course. Please try again.");
				}
			}}
		/>
	);
};

export default CourseFreeCourseClaimModal;
